import {
  CHAT_GET_USER_CHATS_REQUEST,
  CHAT_GET_USER_CHATS_SUCCESS,
  CHAT_GET_USER_CHATS_FAIL,
  CHAT_GET_CHAT_BY_ID_REQUEST,
  CHAT_GET_CHAT_BY_ID_SUCCESS,
  CHAT_GET_CHAT_BY_ID_FAIL,
  CHAT_SEND_MESSAGE_REQUEST,
  CHAT_SEND_MESSAGE_SUCCESS,
  CHAT_SEND_MESSAGE_FAIL,
  CHAT_VOICE_UPLOAD_COMPLETE,
  CHAT_EDIT_MESSAGE_SUCCESS,
  CHAT_DELETE_MESSAGE_SUCCESS,
  CHAT_BULK_DELETE_MESSAGES_SUCCESS,
  CHAT_NEW_MESSAGE,
  CHAT_NEW_CHAT_REQUEST,
  CHAT_REQUEST_ACCEPTED,
  CHAT_REQUEST_REJECTED,
  CHAT_SELECT_CHAT,
  CHAT_LEAVE_ROOM,
  CHAT_SET_TYPING,
  CHAT_ACTIVE_TAB,
  CHAT_RESET,
  CHAT_CREATE_REQUEST_REQUEST,
  CHAT_CREATE_REQUEST_SUCCESS,
  CHAT_CREATE_REQUEST_FAIL,
  CHAT_ACCEPT_REQUEST_SUCCESS,
  CHAT_REJECT_REQUEST_SUCCESS,
  CHAT_MARK_SEEN_SUCCESS,
  CHAT_PIN_MESSAGE_SUCCESS,
  CHAT_UNPIN_MESSAGE_SUCCESS,
  CHAT_MESSAGE_LIKED,
  CHAT_MESSAGE_REACTION,
  CHAT_REMOTE_SEEN,
  CHAT_SEARCH_USERS_REQUEST,
  CHAT_SEARCH_USERS_SUCCESS,
  CHAT_SEARCH_USERS_FAIL,
  CHAT_SEARCH_USERS_CLEAR,
  CHAT_CALL_INCOMING,
  CHAT_CALL_ACCEPTED,
  CHAT_CALL_REJECTED,
  CHAT_CALL_CANCELED,
  CHAT_CALL_DISMISS,
  CHAT_CALL_OUTGOING_STATUS,
  CHAT_ACTIVE_CALL_SET,
  CHAT_ACTIVE_CALL_CLEAR,
  CHAT_CALL_OUTGOING_TYPE,
  CHAT_PRESENCE_UPDATE,
  CHAT_PRESENCE_MERGE,
  CHAT_SET_UNREAD_COUNTS,
  CHAT_BULK_MARK,
  CHAT_BULK_MUTE,
  CHAT_BULK_REMOVE,
  CHAT_LOAD_OLDER_MESSAGES_REQUEST,
  CHAT_LOAD_OLDER_MESSAGES_SUCCESS,
  CHAT_LOAD_OLDER_MESSAGES_FAIL,
} from "./userChatTypes";

const initialState = {
  items: [],
  pagination: { page: 1, limit: 20, total: 0, pages: 0 },
  currentChat: null,
  messagesPagination: null,
  activeChatId: null,
  activeTab: "chat",
  typingUserId: null,
  loadingList: false,
  loadingChat: false,
  loadingOlderMessages: false,
  sendingMessage: false,
  error: null,
  searchResults: [],
  loadingSearch: false,
  searchError: null,
  /** Unread message count per chat id (for badge on list). Cleared when user opens that chat. */
  unreadByChatId: {},
  /** Incoming call: { chatId, callType, roomName, callerId, callerName } or null */
  incomingCall: null,
  /** Outgoing call: 'ringing' | 'accepted' | 'rejected' | null */
  outgoingCallStatus: null,
  /** Call type when we started the call: 'audio' | 'video' */
  outgoingCallType: null,
  /** When in an active 1:1 call: { chatId, roomName, callType } or null */
  activeChatCall: null,
  /** Map userId -> boolean for accepted-chat peers (site-wide socket presence) */
  peerOnlineByUserId: {},
  /** Map userId -> Date/ISO string for last-online timestamp (offline peers only) */
  lastOnlineByUserId: {},
};

export const userChatReducer = (state = initialState, action) => {
  switch (action.type) {
    case CHAT_GET_USER_CHATS_REQUEST:
      return {
        ...state,
        loadingList: true,
        error: null,
      };
    case CHAT_GET_USER_CHATS_SUCCESS: {
      const { items, pagination } = action.payload?.body ?? {};
      return {
        ...state,
        loadingList: false,
        items: items ?? state.items,
        pagination: pagination ?? state.pagination,
        error: null,
      };
    }
    case CHAT_GET_USER_CHATS_FAIL:
      return {
        ...state,
        loadingList: false,
        error: action.payload,
      };

    case CHAT_GET_CHAT_BY_ID_REQUEST:
      return {
        ...state,
        loadingChat: true,
        loadingOlderMessages: false,
        error: null,
      };
    case CHAT_GET_CHAT_BY_ID_SUCCESS: {
      const { chat, messagesPagination } = action.payload?.body ?? {};
      return {
        ...state,
        loadingChat: false,
        currentChat: chat ?? state.currentChat,
        messagesPagination: messagesPagination ?? state.messagesPagination,
        activeChatId: chat?._id ?? state.activeChatId,
        error: null,
      };
    }
    case CHAT_GET_CHAT_BY_ID_FAIL:
      return {
        ...state,
        loadingChat: false,
        error: action.payload,
      };

    case CHAT_LOAD_OLDER_MESSAGES_REQUEST:
      return { ...state, loadingOlderMessages: true, error: null };
    case CHAT_LOAD_OLDER_MESSAGES_SUCCESS: {
      const chatId = action.payload?._chatId;
      if (chatId && String(state.activeChatId) !== String(chatId)) {
        return { ...state, loadingOlderMessages: false };
      }
      const { chat, messagesPagination } = action.payload?.body ?? {};
      const olderMessages = chat?.Messages ?? [];
      const existingMessages = state.currentChat?.Messages ?? [];
      const existingIds = new Set(existingMessages.map((m) => String(m._id)));
      const dedupedOlder = olderMessages.filter(
        (m) => !existingIds.has(String(m._id)),
      );
      const merged = [...dedupedOlder, ...existingMessages];
      return {
        ...state,
        loadingOlderMessages: false,
        currentChat: {
          ...state.currentChat,
          Messages: merged,
        },
        messagesPagination: messagesPagination ?? state.messagesPagination,
        error: null,
      };
    }
    case CHAT_LOAD_OLDER_MESSAGES_FAIL:
      return { ...state, loadingOlderMessages: false, error: action.payload };

    case CHAT_SEND_MESSAGE_REQUEST: {
      const { tempMessage, chatId } = action.payload || {};
      if (!tempMessage || !chatId || !state.currentChat) {
        return { ...state, sendingMessage: true, error: null };
      }
      const cid = String(chatId);
      if (String(state.currentChat._id) !== cid) {
        return { ...state, sendingMessage: true, error: null };
      }
      const messages = [...(state.currentChat.Messages || []), tempMessage];
      return {
        ...state,
        sendingMessage: true,
        currentChat: { ...state.currentChat, Messages: messages },
        error: null,
      };
    }
    case CHAT_SEND_MESSAGE_SUCCESS: {
      const message = action.payload?.body?.message;
      const chatId = action.payload?.body?.chatId;
      const tempId = action.payload?._optimisticTempId;
      const cid = chatId != null ? String(chatId) : null;
      if (
        !message ||
        !state.currentChat ||
        String(state.currentChat._id) !== cid
      ) {
        return { ...state, sendingMessage: false };
      }
      const oldMessages = state.currentChat.Messages || [];
      const tempIndex = tempId
        ? oldMessages.findIndex((m) => String(m._id) === String(tempId))
        : -1;
      const messages =
        tempIndex >= 0
          ? oldMessages.map((m, i) =>
              i === tempIndex ? { ...message } : m,
            )
          : [...oldMessages, message];
      const updatedChat = { ...state.currentChat, Messages: messages };
      const updatedItems = state.items.map((c) =>
        String(c._id) === cid
          ? { ...c, Messages: [message], DefineTime: message.CreatedAt }
          : c,
      );
      return {
        ...state,
        sendingMessage: false,
        currentChat: updatedChat,
        items: updatedItems,
        error: null,
      };
    }
    case CHAT_SEND_MESSAGE_FAIL: {
      const errorMsg = action.payload?.error ?? action.payload;
      const tempId = action.payload?.tempId;
      if (!tempId || !state.currentChat) {
        return { ...state, sendingMessage: false, error: errorMsg };
      }
      const messages = (state.currentChat.Messages || []).map((m) =>
        String(m._id) === String(tempId)
          ? { ...m, _status: "failed", _errorMessage: errorMsg }
          : m,
      );
      return {
        ...state,
        sendingMessage: false,
        currentChat: { ...state.currentChat, Messages: messages },
        error: errorMsg,
      };
    }

    case CHAT_VOICE_UPLOAD_COMPLETE: {
      const { tempId, remoteUrl, chatId } = action.payload || {};
      if (!tempId || !state.currentChat) return state;
      if (chatId && String(state.currentChat._id) !== String(chatId))
        return state;
      const messages = (state.currentChat.Messages || []).map((m) =>
        String(m._id) === String(tempId)
          ? {
              ...m,
              _status: "sending",
              Attachments: [remoteUrl],
              MessageAttachs: [remoteUrl],
            }
          : m,
      );
      return {
        ...state,
        currentChat: { ...state.currentChat, Messages: messages },
      };
    }

    case CHAT_EDIT_MESSAGE_SUCCESS: {
      const body = action.payload?.body;
      const chatId = body?.chatId ?? action.payload?.params?.chatId;
      const messageId = body?.messageId ?? action.payload?.params?.messageId;
      const text = body?.text;
      if (!state.currentChat || !messageId || text == null) return state;
      if (String(state.currentChat._id) !== String(chatId)) return state;
      const now = Date.now();
      const messages = (state.currentChat.Messages || []).map((m) =>
        String(m._id) === String(messageId)
          ? { ...m, Text: text, UpdatedAt: now }
          : m,
      );
      return {
        ...state,
        currentChat: { ...state.currentChat, Messages: messages },
      };
    }
    case CHAT_DELETE_MESSAGE_SUCCESS: {
      const body = action.payload?.body;
      const chatId = body?.chatId ?? action.payload?.params?.chatId;
      const messageId = body?.messageId ?? action.payload?.params?.messageId;
      const hard = body?.hard;
      if (!state.currentChat || !messageId) return state;
      if (String(state.currentChat._id) !== String(chatId)) return state;
      const messages = hard
        ? (state.currentChat.Messages || []).filter(
            (m) => String(m._id) !== String(messageId),
          )
        : (state.currentChat.Messages || []).map((m) =>
            String(m._id) === String(messageId)
              ? { ...m, IsDeleted: true, Text: "" }
              : m,
          );
      return {
        ...state,
        currentChat: { ...state.currentChat, Messages: messages },
      };
    }

    case CHAT_BULK_DELETE_MESSAGES_SUCCESS: {
      const body = action.payload?.body;
      const chatId = body?.chatId;
      const ids = (body?.deletedIds ?? []).map((id) => String(id));
      const hard = body?.hard;
      if (!state.currentChat || !ids.length) return state;
      if (String(state.currentChat._id) !== String(chatId)) return state;
      const messages = hard
        ? (state.currentChat.Messages || []).filter(
            (m) => !ids.includes(String(m._id)),
          )
        : (state.currentChat.Messages || []).map((m) =>
            ids.includes(String(m._id))
              ? { ...m, IsDeleted: true, Text: "" }
              : m,
          );
      return {
        ...state,
        currentChat: { ...state.currentChat, Messages: messages },
      };
    }

    case CHAT_NEW_MESSAGE: {
      const { chatId, message } = action.payload ?? {};
      if (!chatId || !message) return state;
      const cid = String(chatId);
      const isActive = String(state.activeChatId) === cid;
      if (isActive && state.currentChat?.Messages) {
        const alreadyExists = state.currentChat.Messages.some(
          (m) => String(m._id) === String(message._id),
        );
        if (alreadyExists) return state;
      }
      const messages =
        isActive && state.currentChat?.Messages
          ? [...state.currentChat.Messages, message]
          : state.currentChat?.Messages;
      const currentChat =
        isActive && state.currentChat
          ? { ...state.currentChat, Messages: messages ?? [message] }
          : state.currentChat;
      const updatedItems = state.items.map((c) =>
        String(c._id) === cid
          ? { ...c, Messages: [message], DefineTime: message.CreatedAt }
          : c,
      );
      const unreadByChatId = { ...state.unreadByChatId };
      if (!isActive) {
        unreadByChatId[cid] = (unreadByChatId[cid] || 0) + 1;
      }
      return {
        ...state,
        currentChat: currentChat ?? state.currentChat,
        items: updatedItems.length ? updatedItems : state.items,
        unreadByChatId,
      };
    }
    case CHAT_NEW_CHAT_REQUEST: {
      const { chat, chatId } = action.payload ?? {};
      if (!chat && !chatId) return state;
      const newChat = chat || { _id: chatId };
      const exists = state.items.some(
        (c) => String(c._id) === String(newChat._id ?? chatId),
      );
      if (exists) return state;
      return {
        ...state,
        items: [newChat, ...state.items],
      };
    }
    case CHAT_REQUEST_ACCEPTED: {
      const { chatId } = action.payload ?? {};
      if (!chatId) return state;
      const cid = String(chatId);
      const updateAccepted = (c) =>
        c && String(c._id) === cid ? { ...c, RequestStatus: "accepted" } : c;
      return {
        ...state,
        items: state.items.map(updateAccepted),
        currentChat: updateAccepted(state.currentChat),
      };
    }
    case CHAT_REQUEST_REJECTED: {
      const { chatId } = action.payload ?? {};
      if (!chatId) return state;
      const cid = String(chatId);
      const items = state.items.filter((c) => String(c._id) !== cid);
      return {
        ...state,
        items,
        activeChatId:
          String(state.activeChatId) === cid ? null : state.activeChatId,
        currentChat:
          state.currentChat && String(state.currentChat._id) === cid
            ? null
            : state.currentChat,
      };
    }

    case CHAT_SELECT_CHAT: {
      const selectedId = action.payload != null ? String(action.payload) : null;
      const unreadByChatId = { ...state.unreadByChatId };
      if (selectedId) unreadByChatId[selectedId] = 0;
      return {
        ...state,
        activeChatId: action.payload ?? null,
        unreadByChatId,
        loadingOlderMessages: false,
      };
    }
    case CHAT_SET_UNREAD_COUNTS: {
      const incoming = action.payload || {};
      const activeKey = state.activeChatId ? String(state.activeChatId) : null;
      const merged = { ...state.unreadByChatId, ...incoming };
      if (activeKey && merged[activeKey] !== 0 && incoming[activeKey] != null) {
        merged[activeKey] = 0;
      }
      return { ...state, unreadByChatId: merged };
    }
    case CHAT_BULK_MARK: {
      const { chatIds = [], read = true } = action.payload || {};
      const unreadByChatId = { ...state.unreadByChatId };
      if (read) {
        chatIds.forEach((id) => {
          unreadByChatId[String(id)] = 0;
        });
      }
      return { ...state, unreadByChatId };
    }
    case CHAT_BULK_MUTE: {
      const { chatIds = [], mute = true } = action.payload || {};
      const idSet = new Set(chatIds.map((id) => String(id)));
      const items = (state.items || []).map((c) =>
        idSet.has(String(c._id)) ? { ...c, Muted: mute, IsMuted: mute } : c,
      );
      return { ...state, items };
    }
    case CHAT_BULK_REMOVE: {
      const { chatIds = [] } = action.payload || {};
      const idSet = new Set(chatIds.map((id) => String(id)));
      const items = (state.items || []).filter(
        (c) => !idSet.has(String(c._id)),
      );
      const unreadByChatId = { ...state.unreadByChatId };
      chatIds.forEach((id) => {
        delete unreadByChatId[String(id)];
      });
      return { ...state, items, unreadByChatId };
    }
    case CHAT_LEAVE_ROOM: {
      const leaveId = action.payload != null ? String(action.payload) : null;
      return {
        ...state,
        activeChatId:
          leaveId && String(state.activeChatId) === leaveId
            ? null
            : state.activeChatId,
      };
    }
    case CHAT_SET_TYPING:
      return {
        ...state,
        typingUserId: action.payload ?? null,
      };
    case CHAT_ACTIVE_TAB:
      return {
        ...state,
        activeTab: action.payload ?? "chat",
      };
    case CHAT_RESET:
      return initialState;

    case CHAT_PRESENCE_UPDATE: {
      const { userId, online, lastOnlineAt } = action.payload ?? {};
      if (userId == null) return state;
      const id = String(userId);
      const nextPeer = {
        ...state.peerOnlineByUserId,
        [id]: online === true,
      };
      const nextLast = { ...state.lastOnlineByUserId };
      if (online === true) {
        delete nextLast[id];
      } else if (lastOnlineAt != null) {
        nextLast[id] = lastOnlineAt;
      }
      return {
        ...state,
        peerOnlineByUserId: nextPeer,
        lastOnlineByUserId: nextLast,
      };
    }
    case CHAT_PRESENCE_MERGE: {
      const map = action.payload;
      if (!map || typeof map !== "object") return state;
      const nextPeer = { ...state.peerOnlineByUserId };
      const nextLast = { ...state.lastOnlineByUserId };
      for (const [id, val] of Object.entries(map)) {
        const online = val === true || val?.online === true;
        nextPeer[String(id)] = online;
        if (online) {
          delete nextLast[String(id)];
        } else if (val?.lastOnlineAt != null) {
          nextLast[String(id)] = val.lastOnlineAt;
        }
      }
      return {
        ...state,
        peerOnlineByUserId: nextPeer,
        lastOnlineByUserId: nextLast,
      };
    }

    case CHAT_CREATE_REQUEST_REQUEST:
      return { ...state, error: null };
    case CHAT_CREATE_REQUEST_SUCCESS: {
      const body = action.payload?.body;
      const newChat = body?.chat;
      const chatId = body?.chatId ?? newChat?._id;
      const alreadyExists = body?.alreadyExists;
      const items = alreadyExists
        ? state.items
        : newChat
          ? [newChat, ...state.items]
          : state.items;
      return {
        ...state,
        items,
        activeChatId: chatId ?? state.activeChatId,
        currentChat: newChat ?? state.currentChat,
        error: null,
      };
    }
    case CHAT_CREATE_REQUEST_FAIL:
      return { ...state, error: action.payload };

    case CHAT_ACCEPT_REQUEST_SUCCESS: {
      const chatId = action.payload?.body?.chatId;
      if (!chatId) return state;
      const idStr = String(chatId);
      const updateAccepted = (chat) =>
        chat && String(chat._id) === idStr
          ? { ...chat, RequestStatus: "accepted" }
          : chat;
      return {
        ...state,
        items: state.items.map(updateAccepted),
        currentChat: updateAccepted(state.currentChat),
      };
    }
    case CHAT_REJECT_REQUEST_SUCCESS: {
      const chatId = action.payload?.body?.chatId;
      if (!chatId) return state;
      const idStr = String(chatId);
      const items = state.items.filter((c) => String(c._id) !== idStr);
      return {
        ...state,
        items,
        activeChatId:
          String(state.activeChatId) === idStr ? null : state.activeChatId,
        currentChat:
          state.currentChat && String(state.currentChat._id) === idStr
            ? null
            : state.currentChat,
      };
    }

    case CHAT_MARK_SEEN_SUCCESS:
      return state;

    case CHAT_MESSAGE_LIKED: {
      const { chatId, messageId, liked, userId } = action.payload ?? {};
      if (
        !state.currentChat ||
        !messageId ||
        !userId ||
        String(state.currentChat._id) !== String(chatId)
      )
        return state;
      const messages = (state.currentChat.Messages || []).map((m) => {
        if (String(m._id) !== String(messageId)) return m;
        const likes = Array.isArray(m.Likes) ? [...m.Likes] : [];
        const hasUser = likes.some((id) => String(id) === String(userId));
        const nextLikes =
          liked && !hasUser
            ? [...likes, userId]
            : liked
              ? likes
              : likes.filter((id) => String(id) !== String(userId));
        return { ...m, Likes: nextLikes };
      });
      return {
        ...state,
        currentChat: { ...state.currentChat, Messages: messages },
      };
    }

    case CHAT_MESSAGE_REACTION: {
      const { chatId, messageId, reactions } = action.payload ?? {};
      if (
        !state.currentChat ||
        !messageId ||
        !Array.isArray(reactions) ||
        String(state.currentChat._id) !== String(chatId)
      )
        return state;
      const messages = (state.currentChat.Messages || []).map((m) =>
        String(m._id) !== String(messageId)
          ? m
          : { ...m, Reactions: reactions },
      );
      return {
        ...state,
        currentChat: { ...state.currentChat, Messages: messages },
      };
    }

    case CHAT_REMOTE_SEEN: {
      const { chatId, userId, seen } = action.payload ?? {};
      if (
        !state.currentChat ||
        !userId ||
        String(state.currentChat._id) !== String(chatId)
      )
        return state;
      const messages = (state.currentChat.Messages || []).map((m) => {
        const seenBy = Array.isArray(m.SeenBy) ? [...m.SeenBy] : [];
        const hasUser = seenBy.some((id) => String(id) === String(userId));
        if (hasUser) return m;
        const applyToThis =
          seen === "all" ||
          (Array.isArray(seen) &&
            seen.some((id) => String(id) === String(m._id)));
        if (!applyToThis) return m;
        return { ...m, SeenBy: [...seenBy, userId] };
      });
      return {
        ...state,
        currentChat: { ...state.currentChat, Messages: messages },
      };
    }

    case CHAT_PIN_MESSAGE_SUCCESS:
    case CHAT_UNPIN_MESSAGE_SUCCESS: {
      const body = action.payload?.body;
      const chatId = body?.chatId;
      const pinned = body?.PinnedMessageIds ?? [];
      if (
        !state.currentChat ||
        String(state.currentChat._id) !== String(chatId)
      )
        return state;
      return {
        ...state,
        currentChat: { ...state.currentChat, PinnedMessageIds: pinned },
      };
    }

    case CHAT_SEARCH_USERS_REQUEST:
      return {
        ...state,
        loadingSearch: true,
        searchError: null,
      };
    case CHAT_SEARCH_USERS_SUCCESS: {
      const items =
        action.payload?.body?.items ??
        action.payload?.items ??
        [];
      return {
        ...state,
        loadingSearch: false,
        searchResults: Array.isArray(items) ? items : [],
        searchError: null,
      };
    }
    case CHAT_SEARCH_USERS_FAIL:
      return {
        ...state,
        loadingSearch: false,
        searchError: action.payload,
        searchResults: [],
      };
    case CHAT_SEARCH_USERS_CLEAR:
      return {
        ...state,
        searchResults: [],
        searchError: null,
      };

    case CHAT_CALL_INCOMING:
      return {
        ...state,
        incomingCall: action.payload ?? null,
        outgoingCallStatus: null,
      };
    case CHAT_CALL_ACCEPTED:
      return {
        ...state,
        incomingCall: null,
        outgoingCallStatus: "accepted",
      };
    case CHAT_CALL_REJECTED:
      return {
        ...state,
        incomingCall: null,
        outgoingCallStatus: "rejected",
      };
    case CHAT_CALL_CANCELED:
      return {
        ...state,
        incomingCall: state.incomingCall?.chatId === action.payload?.chatId ? null : state.incomingCall,
        outgoingCallStatus: null,
      };
    case CHAT_CALL_DISMISS:
      return {
        ...state,
        incomingCall: null,
      };
    case CHAT_CALL_OUTGOING_STATUS:
      return {
        ...state,
        outgoingCallStatus: action.payload ?? null,
      };
    case CHAT_CALL_OUTGOING_TYPE:
      return { ...state, outgoingCallType: action.payload ?? null };
    case CHAT_ACTIVE_CALL_SET:
      return { ...state, activeChatCall: action.payload ?? null };
    case CHAT_ACTIVE_CALL_CLEAR:
      return {
        ...state,
        activeChatCall: null,
        outgoingCallStatus: null,
        outgoingCallType: null,
      };

    default:
      return state;
  }
};
